import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { createModifierGroup, updateModifierGroup } from '../../../shared/api/products.js'
import type { ModifierGroup } from '../../../shared/types/models.js'
import {
  normalizeSortOrder,
  validateModifierGroupSelection,
  validateNameField,
  validateSortOrderField,
} from '../../../shared/utils/catalog.js'
import { toFieldErrors, type FieldErrors } from '../../../shared/utils/formErrors.js'

const MIN_ERROR: Record<string, string> = {
  invalid: 'groupMinInvalid',
  requiredMin: 'groupRequiredMin',
}

const MAX_ERROR: Record<string, string> = {
  required: 'groupMaxRequired',
  invalid: 'groupMaxInvalid',
  maxLtMin: 'groupMaxLtMin',
  requiredMaxZero: 'groupRequiredMaxZero',
}

interface Props {
  organizationId: string
  productId: string
  group?: ModifierGroup | null
  disabled: boolean
  onDone: () => void
  onCancel: () => void
}

export default function ModifierGroupForm({
  organizationId,
  productId,
  group,
  disabled,
  onDone,
  onCancel,
}: Props) {
  const { t } = useTranslation()
  const isEdit = group !== undefined && group !== null

  const [name, setName] = useState(group?.name ?? '')
  const [min, setMin] = useState(group ? String(group.min) : '0')
  const [max, setMax] = useState(group?.max != null ? String(group.max) : '')
  const [unlimited, setUnlimited] = useState(group?.max == null)
  const [isRequired, setIsRequired] = useState(group?.isRequired ?? false)
  const [sortOrder, setSortOrder] = useState(group ? String(group.sortOrder) : '0')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [saving, setSaving] = useState(false)

  const idSuffix = isEdit && group ? group.id : 'new'

  function clearErrors() {
    if (Object.keys(errors).length > 0) setErrors({})
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (disabled) return

    const fieldErrors: FieldErrors = {}
    if (validateNameField(name) === 'required') fieldErrors.name = t('error.fieldRequired')
    const selection = validateModifierGroupSelection(min, max, unlimited, isRequired)
    if (selection.min) fieldErrors.min = t(`products.${MIN_ERROR[selection.min]}`)
    if (selection.max) fieldErrors.max = t(`products.${MAX_ERROR[selection.max]}`)
    if (validateSortOrderField(sortOrder)) fieldErrors.sortOrder = t('products.sortOrderInvalid')
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    const payload = {
      name: name.trim(),
      min: min.trim() === '' ? 0 : Number(min),
      max: unlimited ? null : Number(max),
      isRequired,
      sortOrder: normalizeSortOrder(sortOrder) ?? 0,
    }

    setSaving(true)
    setErrors({})
    try {
      if (isEdit && group) {
        await updateModifierGroup(organizationId, productId, group.id, payload)
      } else {
        await createModifierGroup(organizationId, productId, payload)
      }
      onDone()
    } catch (err: unknown) {
      setErrors(toFieldErrors(err, ['name', 'min', 'max', 'sortOrder'], t('products.modifierError')))
      setSaving(false)
    }
  }

  return (
    <div className="modifier-form settings-card">
      <h4>{isEdit ? t('products.groupEditTitle') : t('products.groupCreateTitle')}</h4>
      {errors.general && <div className="form-error-banner">{errors.general}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`groupName-${idSuffix}`}>{t('products.name')} *</label>
            <input
              id={`groupName-${idSuffix}`}
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value)
                clearErrors()
              }}
              maxLength={200}
              required
              disabled={saving || disabled}
              autoComplete="off"
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`groupSortOrder-${idSuffix}`}>{t('products.sortOrder')}</label>
            <input
              id={`groupSortOrder-${idSuffix}`}
              type="number"
              min={0}
              step={1}
              value={sortOrder}
              onChange={e => {
                setSortOrder(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.sortOrder && <span className="field-error">{errors.sortOrder}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`groupMin-${idSuffix}`}>{t('products.min')}</label>
            <input
              id={`groupMin-${idSuffix}`}
              type="number"
              min={0}
              step={1}
              value={min}
              onChange={e => {
                setMin(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.min && <span className="field-error">{errors.min}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`groupMax-${idSuffix}`}>{t('products.max')}</label>
            <input
              id={`groupMax-${idSuffix}`}
              type="number"
              min={0}
              step={1}
              value={unlimited ? '' : max}
              onChange={e => {
                setMax(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled || unlimited}
              placeholder={unlimited ? t('products.unlimited') : undefined}
            />
            <label className="modifier-check">
              <input
                id={`groupUnlimited-${idSuffix}`}
                type="checkbox"
                checked={unlimited}
                onChange={e => {
                  setUnlimited(e.target.checked)
                  clearErrors()
                }}
                disabled={saving || disabled}
              />
              <span>{t('products.unlimited')}</span>
            </label>
            {errors.max && <span className="field-error">{errors.max}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`groupRequired-${idSuffix}`}>{t('products.required')}</label>
            <label className="modifier-check">
              <input
                id={`groupRequired-${idSuffix}`}
                type="checkbox"
                checked={isRequired}
                onChange={e => {
                  setIsRequired(e.target.checked)
                  clearErrors()
                }}
                disabled={saving || disabled}
              />
              <span>{t('products.required')}</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || disabled}>
            {saving ? t('products.saving') : isEdit ? t('products.save') : t('products.addGroup')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
            {t('products.cancel')}
          </button>
        </div>
      </form>
    </div>
  )
}